import { useEffect } from 'react'
import { Controller, FieldValues, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import {
  Alert,
  Button,
  Field,
  Input,
  Modal,
  Typography
} from '@matillion/component-library'

import { useProjectInfo } from 'hooks/useProjectInfo/useProjectInfo'

import { usePipelineBrowserCommandProvider } from '../../effects/usePipelineBrowserCommand'
import { folderExists } from '../../reducers/pipelineTreeReducer/pipelineTreeReducer'
import { PipelineTreeReducerType } from '../../reducers/pipelineTreeReducer/types'
import classes from './CreateFolderModal.module.scss'

export interface CreateFolderModalProps {
  path: string
  onClose: () => void
}

/* A valid name has the following criteria:
- any number of trailing spaces
- any number of starting spaces
- includes letters, normal brackets or dashes
- must include at least one valid character
- words can only be separated by one space
*/
const VALID_NAME_REGEX = /^[a-zA-Z0-9-_\s]*$/

const CreateFolderModal = ({ onClose, path }: CreateFolderModalProps) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'create-folder.modal'
  })

  const isCreatingAtRoot = path === ''

  const { branchId, projectId } = useProjectInfo()
  const { pipelineTree, onPipelineTreeCommand } =
    usePipelineBrowserCommandProvider()

  const {
    handleSubmit,
    control,
    setFocus,
    formState: { isDirty, isValid }
  } = useForm({
    mode: 'all'
  })

  useEffect(() => {
    setFocus('folderName')
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const handleAddFolder = (data: FieldValues) => {
    const folderName = data.folderName

    if (isCreatingAtRoot) {
      onPipelineTreeCommand({
        name: folderName,
        branch: branchId,
        project: projectId,
        type: PipelineTreeReducerType.ADD_ROOT_FOLDER
      })
    } else {
      onPipelineTreeCommand({
        path: path,
        name: folderName,
        branch: branchId,
        project: projectId,
        type: PipelineTreeReducerType.ADD_FOLDER
      })
    }
    onClose()
  }

  return (
    <Modal
      size="mid"
      setFocus={false}
      onCancel={onClose}
      disableBackdropClick
      id="create-folder-dialog"
      className={classes.Modal}
      data-testid="create-folder-modal"
      ariaLabelledBy="create-folder-title"
    >
      <Typography format="tm" className={classes.Title}>
        {t('title')}
      </Typography>

      <Typography className={classes.Description}>
        {t('description')}
      </Typography>

      <Alert
        theme="dark"
        type="warning"
        title={t('alert.title')}
        className={classes.Alert}
        message={t('alert.message')}
        data-testid="create-pipeline-folder-alert"
      />

      <form
        onSubmit={(e) => {
          handleSubmit(handleAddFolder)(e)
        }}
      >
        <div className={classes.Body} data-testid="create-folder-form">
          <Controller
            name="folderName"
            defaultValue=""
            control={control}
            rules={{
              required: t('validation.no-empty-string'),
              pattern: {
                value: VALID_NAME_REGEX,
                message: t('validation.failed-regex')
              },
              validate: (name: string) => {
                // A root folder has no path when searching the tree
                const folderPath = isCreatingAtRoot ? undefined : path

                if (name.length > 50) {
                  return t('validation.name-too-long')
                }

                if (folderExists(pipelineTree, name, folderPath)) {
                  return t('validation.already-exists')
                }
              }
            }}
            render={({
              field: { value, onChange, name, ref },
              fieldState: { error }
            }) => {
              return (
                <Field
                  id={name}
                  name={name}
                  value={value}
                  inputRef={ref}
                  error={!!error}
                  hasError={!!error}
                  onChange={onChange}
                  inputComponent={Input}
                  title={t('input.label')}
                  errorText={error?.message}
                  aria-label={t('aria-label')}
                  data-testid="create-folder-input"
                  placeholder={t('input.placeholder')}
                />
              )
            }}
          />

          <div className={classes.Buttons}>
            <Button
              alt="secondary"
              onClick={onClose}
              text={t('cancel')}
              id="create-folder-cancel"
              data-testid="create-folder-cancel"
            />
            <Button
              type="submit"
              alt="positive"
              text={t('submit')}
              id="create-folder-create"
              disabled={!isDirty || !isValid}
              data-testid="create-folder-submit"
            />
          </div>
        </div>
      </form>
    </Modal>
  )
}

export default CreateFolderModal
